import React, { FC } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  DataGrid,
  GridColDef,
  GridToolbarContainer,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
  GridLogicOperator,
} from "@mui/x-data-grid";
import {
  Chip,
  Stack,
  Box,
  Typography,
} from "@mui/material";
import { CopyAllOutlined, Subject } from "@mui/icons-material";

import { IconButton } from "@components/base";
import { useGetStakeholders } from "@api";
import {
  QuestionAnswerRow,
  useGetQuestionAnswers,
} from "@api/hooks/doubleMateriality/GetQuestionAnswers/useGetQuestionAnswers";
import { useUserStore } from "@store/user";
import { getYearFromSearchParams } from "@helpers";

interface ResultsTableRow {
  id: string;
  companyName: string;
  fullName: string;
  email: string;
  deadline: string | null;
  surveyLink: string;
  status: string;
  statusColor: "success" | "warning" | "error" | "default";
}

const StakeholdersResultsTable: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useUserStore();

  const [searchParams] = useSearchParams();
  const year = getYearFromSearchParams(searchParams);
  const step = searchParams.get("step") || "4";

  const { stakeholders } = useGetStakeholders(user, {
    pageNumber: 1,
    pageSize: 1000,
    year,
  });
  const { answers } = useGetQuestionAnswers(user);

  const stakeholdersList = stakeholders?.data ?? [];
  const answersList = (answers ?? []) as QuestionAnswerRow[];

  const stakeholderStats = new Map<string, { total: number; answered: number }>();

  answersList.forEach((answer) => {
    const stakeholderId = answer.stakeholderId;

    if (!stakeholderId) return;

    const stats = stakeholderStats.get(stakeholderId) ?? { total: 0, answered: 0 };

    stats.total += 1;
    if (answer.answeredAt) stats.answered += 1;
    stakeholderStats.set(stakeholderId, stats);
  });

  const stakeholdersWithAssignedQuestions = stakeholdersList.filter((stakeholder) =>
    stakeholderStats.has(stakeholder.id),
  );

  const currentDomain = location.origin;
  const resultsData: ResultsTableRow[] = stakeholdersWithAssignedQuestions.map(
    (stakeholder) => {
      const stats = stakeholderStats.get(stakeholder.id) ?? {
        total: 0,
        answered: 0,
      };
      const hasAssignments = stats.total > 0;
      const hasCompleted = hasAssignments && stats.answered === stats.total;
      const hasProgress = stats.answered > 0 && !hasCompleted;

      let statusColor: ResultsTableRow["statusColor"] = "default";
      let statusLabel = t("doubleMateriality.notAnswered", "Not answered");
      let countLabel: string | null = null;

      if (hasCompleted) {
        statusColor = "success";
        statusLabel = t("doubleMateriality.completed", "Completed");
        countLabel = `${stats.answered}/${stats.total}`;
      } else if (hasProgress) {
        statusColor = "warning";
        statusLabel = t("doubleMateriality.inProgress", "In progress");
        countLabel = `${stats.answered}/${stats.total}`;
      } else if (hasAssignments) {
        statusColor = "error";
        countLabel = `0/${stats.total}`;
      }

      return {
        id: stakeholder.id,
        companyName: stakeholder.companyName,
        fullName: stakeholder.fullName,
        email: stakeholder.email,
        deadline: stakeholder.deadline,
        surveyLink: `${currentDomain}/dm-survey/${stakeholder.id}`,
        statusColor,
        status: countLabel ? `${statusLabel} (${countLabel})` : statusLabel,
      };
    },
  );

  const handleShowAnswers = (stakeholderId: string): void => {
    navigate(
      `/csrd/double-materiality/answers/${stakeholderId}?year=${year}&step=${step}`,
    );
  };


  // Custom Toolbar
  const CustomToolbar = (): JSX.Element => {
    return (
      <GridToolbarContainer
        sx={{
          display: "flex",
          justifyContent: "space-between",
          p: 2,
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <GridToolbarQuickFilter />
          <GridToolbarFilterButton />
        </Stack>
      </GridToolbarContainer>
    );
  };

  const columns: GridColDef<ResultsTableRow>[] = [
    {
      field: "companyName",
      headerName: t("stakeholders.table.companyName", "Company name"),
      width: 220,
      renderCell: (params) => (
        <Typography variant="body2">{params.value || "-"}</Typography>
      ),
    },
    {
      field: "fullName",
      headerName: t("stakeholders.fields.fullName", "Full name"),
      width: 250,
      renderCell: (params) => (
        <Stack direction="row" alignItems="center" spacing={1} height="100%">
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: "50%",
              bgcolor: "primary.main",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              fontSize: "0.875rem",
              fontWeight: "bold",
            }}
          >
            {params.value.charAt(0).toUpperCase()}
          </Box>
          <Typography variant="body2">{params.value}</Typography>
        </Stack>
      ),
    },
    {
      field: "email",
      headerName: t("stakeholders.fields.email", "Email"),
      width: 250,
    },
    {
      field: "deadline",
      headerName: t(
        "stakeholders.fields.deadline",
        "Deadline for completing the questionnaire",
      ),
      width: 250,
      valueFormatter: (value) =>
        value ? new Date(value).toLocaleDateString() : "-",
    },
    {
      field: "surveyLink",
      headerName: t("doubleMateriality.surveyLink", "Survey link"),
      width: 120,
      align: "center",
      headerAlign: "center",
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <IconButton
          size="small"
          onClick={() => {
            navigator.clipboard.writeText(params.value);
          }}
          title={t("doubleMateriality.copySurveyLink", "Copy survey link")}
        >
          <CopyAllOutlined fontSize="small" />
        </IconButton>
      ),
    },
    {
      field: "status",
      headerName: t("doubleMateriality.status", "Status"),
      width: 180,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={params.row.statusColor}
          variant={params.row.statusColor === "success" ? "filled" : "outlined"}
          size="small"
        />
      ),
    },
    {
      field: "actions",
      headerName: t("doubleMateriality.showAnswers", "Show answers"),
      width: 140,
      align: "center",
      headerAlign: "center",
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <IconButton
          size="small"
          onClick={() => handleShowAnswers(params.row.id)}
          title={t("doubleMateriality.showAnswers", "Show answers")}
          color="primary"
        >
          <Subject fontSize="small" />
        </IconButton>
      ),
    },
  ];

  return (
    <Box sx={{ width: "100%", height: 600 }}>
      <DataGrid
        rows={resultsData}
        columns={columns}
        slots={{
          toolbar: CustomToolbar,
          noRowsOverlay: () => (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%", p: 2 }}>
              <Typography>{t("common.noData", "No data available")}</Typography>
            </Box>
          )
        }}
        hideFooterPagination
        initialState={{
          filter: {
            filterModel: {
              items: [],
              logicOperator: GridLogicOperator.Or,
            },
          },
        }}
        disableRowSelectionOnClick
        sx={{
          border: 0,
          '& .MuiDataGrid-cell': {
            display: 'flex',
            alignItems: 'center',
            py: 1
          },
          "& .MuiDataGrid-columnHeader": {
            backgroundColor: "#f5f5f5",
          },
        }}
      />
    </Box>
  );
};

export { StakeholdersResultsTable };
